import { AlreadyPlottedPlayer, ChemistryPlayerPositions, TeamChemistry } from "../types/IOption";
import AddIcon from "../assets/icons/AddIcon.png";
import Alert from "../assets/icons/Alert.png";
import { Theme } from "../styles/Theme";
import * as d3 from "d3";
import {
  appendText,
  appendCircle,
  drawMiddleRect,
  getColorByValue,
  drawPlayerHoverIcons,
  drawPlayerNames,
  drawPlayerImages,
} from "./pitchWidgetUtils";
import { circlePositionLogic, ICirclePositions } from "./formationCirclePositions";
import { useSquadSystemFitContext } from "../contexts/SquadSystemFitContext";
import { ISynergyData } from "../pages/Chemistry/Utils";

const getIndividuallySelectedPlayer = (teamChemistry: TeamChemistry[]): number | null => {
  if (!teamChemistry?.length) return null;
  const firstPlayer = teamChemistry[0].player1;
  const secondPlayer = teamChemistry[0].player2;
  const isIndividuallySelected = teamChemistry.every(conn => conn.player2 === firstPlayer || conn.player1 === firstPlayer);
  const isIndividuallySelectedSecond = teamChemistry.every(conn => conn.player2 === secondPlayer || conn.player1 === secondPlayer);
  return isIndividuallySelected ? firstPlayer : isIndividuallySelectedSecond ? secondPlayer : null;
};

export const drawChemistrys = (
  svg: d3.Selection<SVGGElement, any, null, undefined>,
  xScale: d3.ScaleLinear<number, number>,
  yScale: d3.ScaleLinear<number, number>,
  positionResults: ChemistryPlayerPositions[],
  alreadyPlottedPlayer: AlreadyPlottedPlayer,
  setIsIndividualChemistry: React.Dispatch<React.SetStateAction<boolean>>,
  handleIndividualChemistry: (playerID: number, positionCode: string) => void,
  isIndividualChemistry: boolean,
  setSwapHoverPlayer: React.Dispatch<React.SetStateAction<number>>,
  swappedPlayerClub: string,
  otherClubPlayer: number,
  setSidebarPlayerShow: React.Dispatch<React.SetStateAction<boolean>>,
  setIsReplacePlayer: React.Dispatch<React.SetStateAction<boolean>>,
  setSwapPositionCode: React.Dispatch<React.SetStateAction<string>>,
  setPlayerCardID: React.Dispatch<React.SetStateAction<number>>,
  teamChemistry: TeamChemistry[],
  getChemistrybetweenPlayers: (player1: number, player2: number) => Promise<void>,
  isPerformancSwitchToggled: boolean,
  handleReset: () => void,
  formationName: string,
  theme: Theme,
  playerCardID: number,
  oldPlayerId?: number,
  newPlayerId?: number,
  handleSwapPlayer?: (playerToSwapWith: number, playerToGoOut?: number, swapPosition?: string, secondSwapPlayer?: ChemistryPlayerPositions) => Promise<void>,
  selectedByPlayerPair?: string,
  synergyData?: ISynergyData[],
  isForSimulation?: boolean
) => {

  // const {selectedByPlayerPair,synergyData} = useSquadSystemFitContext();

  if (!Array.isArray(positionResults) || !Array.isArray(teamChemistry)) {
    return;
  }

  const linesGroup = svg.append("g").attr("class", "lines-group");
  const blankPlayers = positionResults.filter((player) => player.playerId <= 0);
  const circleSize = window.innerWidth < 800 || window.innerHeight < 800 ? 18 : 25;
  const imageSize = window.innerWidth < 800 || window.innerHeight < 800 ? 33 : 50;

  positionResults.map((player) => {
    const centerX = xScale(player.xCoordinate);
    const centerY = yScale(player.yCoordinate);
    svg.append("clipPath").attr("id", "playerImageClipPath");

    //draw player images :
    if (player.playerId !== alreadyPlottedPlayer?.playerID) {
      drawPlayerImages(
        // @ts-ignore
        svg,
        xScale,
        yScale,
        setSwapHoverPlayer,
        player,
        positionResults,
        theme,
        isIndividualChemistry,
        alreadyPlottedPlayer,
        handleReset,
        setIsIndividualChemistry,
        handleIndividualChemistry,
        handleSwapPlayer!
      );
    }
    //swap transition :
    if (player.playerId > 0 && player.playerId === alreadyPlottedPlayer?.playerID && blankPlayers.length > 0) {
      svg
        .select("#playerImageClipPath")
        .append("circle")
        .attr("r", circleSize)
        .attr("cy", yScale(alreadyPlottedPlayer.yPosition))
        .attr("cx", xScale(alreadyPlottedPlayer.xPosition))
        .transition()
        .duration(600)
        .attr("cy", centerY)
        .attr("cx", centerX);

      svg
        .append("circle")
        .attr("r", circleSize)
        .style("fill", player.playerId > 0 ? "#D9D9D9" : "none")
        .attr("stroke-width", 1)
        .attr("stroke", theme.playerCardBorderColor)
        .attr("stroke-dasharray", player.playerId > 0 ? "none" : "5,5")
        .attr("cy", yScale(alreadyPlottedPlayer.yPosition))
        .attr("cx", xScale(alreadyPlottedPlayer.xPosition))
        .transition()
        .duration(600)
        .attr("cy", centerY)
        .attr("cx", centerX);

      svg
        .append("image")
        .attr("xlink:href", player.playerImageURL)
        .attr("width", imageSize)
        .attr("height", imageSize)
        // .attr("clip-path", "url(#playerImageClipPath)")
        .attr("cursor", "pointer")
        .on("mouseover", () => {
          showHoverIcons();
          setSwapHoverPlayer(player.playerId);
        })
        .on("mouseout", () => {
          hideHoverIcons();
          setSwapHoverPlayer(0);
        })
        .on("click", () => {
          if (isIndividualChemistry) {
            handleReset();
            setIsIndividualChemistry(false);
          } else {
            handleIndividualChemistry(player.playerId, player.positionCode);
          }
        })
        .on("mousedown", function (event) {
          var longPressTimer = setTimeout(function () {
            showHoverIcons();
          }, 100);
          d3.select(document).on("mouseup", function () {
            clearTimeout(longPressTimer);
          });
        })
        .attr("y", yScale(alreadyPlottedPlayer.yPosition) - imageSize / 2)
        .attr("x", xScale(alreadyPlottedPlayer.xPosition) - imageSize / 2)
        .transition()
        .duration(600)
        .attr("y", centerY - imageSize / 2)
        .attr("x", centerX - imageSize / 2);
    }

    //append empty circle with dash stroke and add icon
    if (!player.playerId || player.playerId <= 0) {
      appendCircle(svg, centerX, centerY, circleSize, "none", "5,5", 1, theme.playerCardBorderColor);
      svg
        .append("image")
        .attr("xlink:href", () => AddIcon)
        .attr("x", xScale(player.xCoordinate) - 24 / 2)
        .attr("y", yScale(player.yCoordinate) - 24 / 2)
        .attr("width", 24)
        .attr("height", 24)
        .on("click", () => {
          setSidebarPlayerShow(true);
          setIsReplacePlayer(true);
          setSwapHoverPlayer(player.playerId);
          setSwapPositionCode(player.positionCode);
        })
        .style("cursor", "pointer")
        .style("pointer-events", "none");
    }

    //display club image on individual player image
    swappedPlayerClub?.length > 5 &&
      otherClubPlayer > 0 &&
      otherClubPlayer === player.playerId &&
      svg
        .append("svg:image")
        .attr("xlink:href", swappedPlayerClub)
        .attr("x", centerX + 12)
        .attr("y", centerY - 70 / 2)
        .attr("width", 22)
        .attr("height", 22);

    const showHoverIcons = () => {
      svg.selectAll(`image.hoverIcon${player.playerId}`).style("opacity", 1);
      svg.selectAll(`rect.hoverRect${player.playerId}`).style("opacity", 1);
    };
    const hideHoverIcons = () => {
      svg.selectAll(`image.hoverIcon${player.playerId}`).style("opacity", 0);
      svg.selectAll(`rect.hoverRect${player.playerId}`).style("opacity", 0);
    };
    
    if (player.playerId > 0 && playerCardID === 0) {
      drawPlayerHoverIcons(
        isIndividualChemistry ? "minus" : "systemFit",
        // @ts-ignore
        svg,
        player,
        xScale,
        yScale,
        theme,
        setSwapHoverPlayer,
        setSidebarPlayerShow,
        setSwapPositionCode,
        setPlayerCardID,
        isForSimulation
      );
    }
  });
  //dashed line when predictive chemistry
  // teamChemistry?.forEach((connection) => {
  //   const startPlayer = positionResults.find((player) => player.playerId === connection.player1);
  //   const endPlayer = positionResults.find((player) => player.playerId === connection.player2);
  //   if (startPlayer && endPlayer) {
  //     linesGroup
  //       .append("line")
  //       .attr("x1", xScale(startPlayer.xCoordinate))
  //       .attr("y1", yScale(startPlayer.yCoordinate))
  //       .attr("x2", xScale(endPlayer.xCoordinate))
  //       .attr("y2", yScale(endPlayer.yCoordinate))
  //       .style("stroke",getColorByValue(connection.score))
  //       //   connection.score > 0 ? (connection.isPredictive && getColorByValue(connection.score) === "#C9F0B6") ? "#B7DAA6" : getColorByValue(connection.score) : startPlayer.playerId > 0 ? theme.NumberColor : theme.playerDataBorder
  //       // )
  //       .style("stroke-width", 2.8)
  //       .style("stroke-dasharray", connection.isPredictive ? "5,5" : "none");
  //   }
  // });

  
  const updatedTeamChemistriys = teamChemistry.map(tc => {
    if (tc.isPredictive && synergyData && Array.isArray(synergyData)) {
      const match = synergyData.find(sd =>
        (sd.player1 === tc.player1 && sd.player2 === tc.player2) ||
        (sd.player1 === tc.player2 && sd.player2 === tc.player1)
      );
  
      if (match) {
        return { ...tc, score: match.score }; // update score
      }
    }
    return tc; // no change
  });

  const teamChemistryData = selectedByPlayerPair === "style" ? updatedTeamChemistriys : teamChemistry

  //dashed line when predictive chemistry
  teamChemistryData?.forEach((connection) => {
    const startPlayer = positionResults.find((player) => player.playerId === connection.player1);
    const endPlayer = positionResults.find((player) => player.playerId === connection.player2);
    if (startPlayer && endPlayer) {
      linesGroup
        .append("line")
        .attr("x1", xScale(startPlayer.xCoordinate))
        .attr("y1", yScale(startPlayer.yCoordinate))
        .attr("x2", xScale(endPlayer.xCoordinate))
        .attr("y2", yScale(endPlayer.yCoordinate))
        .style("stroke",getColorByValue(connection.score))
        //   connection.score > 0 ? (connection.isPredictive && getColorByValue(connection.score) === "#C9F0B6") ? "#B7DAA6" : getColorByValue(connection.score) : startPlayer.playerId > 0 ? theme.NumberColor : theme.playerDataBorder
        // )
        .style("stroke-width", 2.8)
        .style("stroke-dasharray", connection.isPredictive ? "5,5" : "none");
    }
  });

  //draw Player Name :
  if (positionResults.length > 1) {
    const individuallySelectedPlayer = getIndividuallySelectedPlayer(teamChemistry);
    //console.log(individuallySelectedPlayer)
    // @ts-ignore
    drawPlayerNames(linesGroup, xScale, yScale, positionResults, theme, isPerformancSwitchToggled, individuallySelectedPlayer, oldPlayerId, newPlayerId);
  }

  //loop for displaying connection content between players
  teamChemistryData?.forEach((connection) => {
    const startPlayer = positionResults.find((player) => player.playerId === connection.player1);
    const endPlayer = positionResults.find((player) => player.playerId === connection.player2);
    const circlePositionRule = circlePositionLogic[formationName as ICirclePositions]?.find(
      (rule) =>
        (startPlayer?.positionCode === rule.from || startPlayer?.positionCode === rule.to) &&
        (endPlayer?.positionCode === rule.from || endPlayer?.positionCode === rule.to)
    );

     


    //all content between two players
    if (startPlayer && endPlayer) {
      const centerX = (xScale(startPlayer.xCoordinate) + xScale(endPlayer.xCoordinate)) / 2;
      const centerY = (yScale(startPlayer.yCoordinate) + yScale(endPlayer.yCoordinate)) / 2;
      const offsetX = (xScale(circlePositionRule?.circlePosition === "othersided" ? endPlayer.xCoordinate : startPlayer.xCoordinate) + centerX) / 2;
      const offsetY = (yScale(circlePositionRule?.circlePosition === "othersided" ? endPlayer.yCoordinate : startPlayer.yCoordinate) + centerY) / 2;
      // const X = distance > 170 ? (offsetX + xScale(endPlayer.xCoordinate)) / 2 : centerX;
      // const Y = distance > 170 ? (offsetY + yScale(endPlayer.yCoordinate)) / 2 : centerY;
      const X = circlePositionRule?.circlePosition === "sided" || circlePositionRule?.circlePosition === "othersided" ? offsetX : circlePositionRule?.circlePosition === "offsided" ? (offsetX + xScale(endPlayer.xCoordinate)) / 2 : centerX;
      const Y = circlePositionRule?.circlePosition === "sided" || circlePositionRule?.circlePosition === "othersided" ? offsetY : circlePositionRule?.circlePosition === "offsided" ? (offsetY + yScale(endPlayer.yCoordinate)) / 2 : centerY;
      let rectTouched: boolean = false;
      let rectRemoveTimeout: ReturnType<typeof setTimeout>;

      // functions for show no data info
      const showInfo = () => {
        svg
          .append("rect")
          .attr("class", `infoRect${startPlayer.playerId}${endPlayer.playerId}`)
          .attr("width", 110)
          .attr("height", 70)
          .attr("x", X - 110 / 2)
          .attr("y", Y - 70 / 2 - 50)
          .style("fill", theme.NumberColor)
          .attr("rx", 5)
          .style("stroke-width", 1)
          .style("stroke", theme.NumberColor)
          .style("opacity", 1)
          .style("cursor", "pointer")
          .on("mouseover", function () {
            rectTouched = true;
            clearTimeout(rectRemoveTimeout);
          })
          .on("mouseout", function () {
            rectTouched = false;
            svg.selectAll(`rect.infoRect${startPlayer.playerId}${endPlayer.playerId}`).remove();
            svg.selectAll(`text.infoText${startPlayer.playerId}${endPlayer.playerId}`).remove();
          })
          .on("click", () => {
            if (svg.select(`rect.infoRect${startPlayer.playerId}${endPlayer.playerId}`).style("opacity") === "1") {
              getChemistrybetweenPlayers(startPlayer.playerId, endPlayer.playerId);
            }
          });

          appendText(svg,X,Y - 67,"NOT ENOUGH","white","Aeonik",400,11,1,"middle",`infoText${startPlayer.playerId}${endPlayer.playerId}`,undefined);
          appendText(svg,X,Y - 53,"DATA TO SCORE","white","Aeonik",400,11,1,"middle",`infoText${startPlayer.playerId}${endPlayer.playerId}`,undefined);

        svg
          .append("rect")
          .attr("class", `infoRect${startPlayer.playerId}${endPlayer.playerId}`)
          .attr("width", 100)
          .attr("height", 25)
          .attr("x", X - 100 / 2)
          .attr("y", Y - 25 / 2 - 33)
          .style("fill", "white")
          .attr("rx", 2)
          .style("stroke-width", 0)
          .style("stroke", "none")
          .style("opacity", 1)
          .attr("pointer-events", "none");

        appendText(svg,X,Y - 28,"Predict score","black","Aeonik",600,13,1,"middle",`infoText${startPlayer.playerId}${endPlayer.playerId}`,"pointer");
      };

      const hideInfo = () => {
        if (!rectTouched) {
          rectRemoveTimeout = setTimeout(() => {
            svg.selectAll(`rect.infoRect${startPlayer.playerId}${endPlayer.playerId}`).remove();
            svg.selectAll(`text.infoText${startPlayer.playerId}${endPlayer.playerId}`).remove();
          }, 100);
        }
      };

      //display no score circle and image
      startPlayer.playerId > 0 &&
        connection.score === 0 &&
        linesGroup
          .append("circle")
          .attr('class', 'scoreCircle')
          .attr("cx", X)
          .attr("cy", Y)
          .attr("r", 14)
          .style("fill", theme.NumberColor)
          .on("mouseover", function () {
            showInfo();
          })
          .on("mouseout", function () {
            hideInfo();
          });
      startPlayer.playerId > 0 &&
        connection.score === 0 &&
        linesGroup
          .append("svg:image")
          .attr("class", 'scoreText')
          .attr("xlink:href", () => Alert)
          .attr("x", X - 20 / 2)
          .attr("y", Y - 20 / 2)
          .attr("width", 20)
          .attr("height", 20)
          .style("pointer-events", "none");
      
      //display circle for score
      connection.score > 0 &&
        linesGroup
          .append("circle")
          .attr('class', 'scoreCircle')
          .attr("r", 16)
          .style("fill", getColorByValue(connection.score))
          .attr("stroke-width", 0)
          .attr("stroke", "none")
          .attr("stroke-dasharray", "none")
          .attr("cx", X)
          .attr("cy", Y);

      //display score text
      connection.score > 0 &&
        appendText(linesGroup,connection.score >= 99 ? X - 3 : X, Y + 4 , (connection.score === 100 ? 99: connection.score), connection.score <= 39.99 ? "#FFFFFF" : "#111111","DM Mono",
          500,12,1,"middle",`scoreText${connection.player1 + connection.player2} scoreText`,"pointer");

      connection.score >= 99 &&
        linesGroup
          .append("circle")
          .attr("cx", X + 10)
          .attr("cy", Y)
          .attr("r", 3)
          .style("fill", "#F2E94E")
          .style("pointer-events", "none")
          .attr("class", `scoreText${connection.player1 + connection.player2} scoreText`)
    } else {
      console.warn("Start player or end player not found for connection:", connection);
    }

  });
};


export const hasPlayersFromDifferentClubs = ({positionResults, teamId}: {positionResults: ChemistryPlayerPositions[], teamId: number}) => {
  if (!positionResults || positionResults.length === 0 || !teamId) return false;
  return positionResults.some(player => player.teamId && player.teamId !== teamId);
}